import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMyBilling } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Receipt, CreditCard, Mail } from "lucide-react";

const StatusPill = ({ status }) => {
    const map = {
        paid: ["bg-[#E1FF01] text-[#0A0A0A]", "Paid"],
        pending: ["bg-white/10 text-white border border-white/20", "Pending"],
        failed: ["bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30", "Failed"],
        expired: ["bg-white/5 text-white/50 border border-white/10", "Expired"],
    };
    const [cls, label] = map[status] || ["bg-white/5 text-white/50 border border-white/10", status || "—"];
    return (
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${cls}`}>
            {label}
        </span>
    );
};

const BillingPage = () => {
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const [data, setData] = useState({ transactions: [] });
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login", { state: { next: "/billing" } });
            return;
        }
        fetchMyBilling()
            .then(setData)
            .catch(() => {})
            .finally(() => setBusy(false));
    }, [user, loading, navigate]);

    return (
        <div data-testid="billing-page" className="min-h-[100dvh] bg-[#0A0A0A] text-white pb-32">
            <header className="px-5 pt-6 pb-3 flex items-center gap-3 border-b border-neutral-900">
                <button
                    data-testid="back-button"
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                >
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-0.5">
                        Billing
                    </p>
                    <h1 className="font-display text-3xl uppercase leading-none">Receipts.</h1>
                </div>
            </header>

            <div className="px-5 pt-5 space-y-4">
                <button
                    data-testid="update-card-cta"
                    onClick={() => navigate("/support")}
                    className="w-full bg-[#141414] hover:bg-[#1a1a1a] border border-neutral-800 hover:border-[#E1FF01]/30 rounded-xl p-4 flex items-center gap-3 transition text-left"
                >
                    <span className="w-10 h-10 rounded-full bg-[#E1FF01]/10 flex items-center justify-center">
                        <CreditCard size={18} className="text-[#E1FF01]" />
                    </span>
                    <div className="flex-1">
                        <p className="font-semibold text-sm">Update card or request refund</p>
                        <p className="text-[10px] text-white/50 uppercase tracking-widest">
                            Reach out to support — we'll handle it
                        </p>
                    </div>
                    <span className="text-white/40">→</span>
                </button>

                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 px-1 pt-2">
                    Past transactions
                </p>

                {busy ? (
                    <p className="text-sm text-white/50">Loading…</p>
                ) : data.transactions.length === 0 ? (
                    <div data-testid="billing-empty" className="text-center py-12">
                        <Receipt size={36} className="mx-auto text-white/30 mb-3" />
                        <p className="font-display text-2xl uppercase">No receipts yet</p>
                        <p className="text-sm text-white/50 mt-1">
                            Your Stripe payment history will show up here.
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-2.5">
                        {data.transactions.map((t) => (
                            <li
                                key={t.session_id}
                                data-testid={`txn-${t.session_id}`}
                                className="bg-[#141414] border border-neutral-800 rounded-2xl p-4"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-display text-xl">
                                            ${(t.amount || 0).toFixed(2)}{" "}
                                            <span className="text-xs text-white/50">
                                                {(t.currency || "usd").toUpperCase()}
                                            </span>
                                        </p>
                                        <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
                                            {new Date(t.created_at).toLocaleString()}
                                        </p>
                                    </div>
                                    <StatusPill status={t.payment_status} />
                                </div>
                                <ul className="mt-2 pt-2 border-t border-neutral-800 space-y-1">
                                    {(t.items || []).map((it) => (
                                        <li
                                            key={`${it.workout_id}:${it.tier_id}`}
                                            className="flex items-center gap-2 text-xs text-white/70"
                                        >
                                            <img
                                                src={it.image_url}
                                                alt=""
                                                className="w-8 h-8 rounded-md object-cover"
                                            />
                                            <span className="flex-1 truncate">{it.workout_title}</span>
                                            <span className="text-[10px] uppercase tracking-widest text-white/40">
                                                {it.tier_name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-[9px] text-white/30 font-mono truncate">
                                    {t.session_id}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}

                <a
                    href="mailto:support@flexfeed.app"
                    data-testid="billing-mailto"
                    className="w-full bg-white/5 border border-white/10 hover:border-[#E1FF01]/30 rounded-xl p-3 flex items-center gap-3 mt-2 transition"
                >
                    <Mail size={16} className="text-[#E1FF01]" />
                    <span className="text-xs uppercase tracking-widest font-semibold">
                        Email a billing question
                    </span>
                </a>
            </div>
        </div>
    );
};

export default BillingPage;
